/**
 * /forgot-password, request a password reset link.
 *
 * Email-only form rendered inside AuthShell. POSTs to /api/forgot-password.
 * Better Auth returns 200 regardless (anti-enumeration), so success state is
 * always "Check your email" with a Resend affordance.
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { forgotPassword, ApiError } from '../lib/api.ts';
import { AuthShell } from '../components/AuthShell.tsx';

export function ForgotPassword() {
  // SIGNUP-FIX (2026-08-10): prefill email from ?email= when arriving
  // from SignUp's "email already exists" callout. Mirrors the pattern
  // in SignIn.tsx (`emailHint`).
  const location = useLocation();
  const emailHint = new URLSearchParams(location.search).get('email') ?? '';
  const [email, setEmail] = useState(emailHint);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (email.trim() === '') {
      setError('Email is required.');
      return;
    }
    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We couldn't send a reset email. Try again in a moment.");
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`If an account exists for ${email}, we just sent a password reset link. The link expires in 24 hours.`}
        footer={
          <>
            Didn't get it?{' '}
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                setSubmitted(false);
                setSubmitting(false);
              }}
            >
              Resend
            </button>{' '}
            ·{' '}
            <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
              Back to sign in
            </Link>
          </>
        }
      >
        {/* Push #84 round 19 (Ean dogfood 2026-06-22). softer
           success card. The previous .callout-success with green
           left-border read as a "form validation" element; this is
           more "your email is on its way" calm guidance. Plain text
           on muted background, no chrome shouting at the user. */}
        <p
          style={{
            margin: 0,
            padding: '12px 16px',
            background: 'var(--olytic-surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: 'var(--fg-muted)',
            fontSize: 13,
            lineHeight: 1.55,
          }}
        >
          Check your inbox (and spam folder) for an email from Olytic. Click the
          link to set a new password.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send a link to set a new one."
      footer={
        <>
          Remembered it?{' '}
          <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-row">
          <label className="form-label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
            disabled={submitting}
            // Round 6 Item 19 — focus first input on mount.
            autoFocus
          />
        </div>

        {error !== null && (
          <div className="callout callout-danger">
            <p className="callout-body">{error}</p>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={submitting}
        >
          {submitting ? (
            <span className="loading-inline">
              <span className="spinner" /> Sending…
            </span>
          ) : (
            'Send reset link'
          )}
        </button>
      </form>
    </AuthShell>
  );
}
